import React, { useState } from 'react';
import ChangeTicketName from '../Modals/ChangeTicketName';
import ScreeningFrequency from '../Modals/ScreeningFrequency';
import ApprovalStatus from '../Modals/ApprovalStatus';
import DeleteCommonModal from '../Modals/DeleteModal';
import { Modal } from '@mui/material';
import CasesAPI from 'api/CasesAPI';
import { canScheduleTicket, isMonitoredTariffTicket } from 'utils/reScreening';
import { useHistory } from 'react-router-dom';
import { useSnackbar } from 'notistack';
import NotificationsModal from '../Modals/NotificationModal';
import {
  handleSettingsNotification,
  userDeleteTicketNotification,
} from 'utils/NotificationsFuncs';

interface ISettingsProps {
  cur_case: any;
  trigger: boolean;
  setTrigger: any;
  setAutoTab: any;
  cur_user: any;
}

const Settings: React.FC<ISettingsProps> = (props: ISettingsProps) => {
  const { cur_case, trigger, setTrigger, setAutoTab, cur_user } = props;
  const [ isModalOpen, setModalOpen ] = useState(false);
  const [ isFrequencyModalOpen, setIsFrequencyModalOpen ] = useState(false);
  const [ isApprovalModalOpen, setIsApprovalModalOpen ] = useState(false);
  const [ isDeleteModalOpen, setIsDeleteModalOpen ] = useState(false);
  const [ isNotificationOpen, setIsNotificationOpen ] = useState(false);
  const [ selectedOptsType, setSelectedOptsType ] = useState(
    cur_case.data.notOptions,
  );

  const history = useHistory();
  const { enqueueSnackbar } = useSnackbar();

  const canSchedule = canScheduleTicket(
    cur_case.data.docType,
    cur_case.data.tariffJurisdiction,
  );
  const isTariffMonitor = isMonitoredTariffTicket(
    cur_case.data.docType,
    cur_case.data.tariffJurisdiction,
  );
  // A ticket that was never on a schedule never chose a date preference (the
  // save default was 'original'), so offer today's date until it has.
  const savedDateStrategy = cur_case.data.tariffResults?.inputs?.dateStrategy;
  const tariffDateStrategy: 'current' | 'original' =
    cur_case.data.frequency &&
    cur_case.data.frequency !== 'Once' &&
    savedDateStrategy === 'original' ?
      'original' :
      'current';

  const handleCloseModal = () => {
    setModalOpen(false);
  };
  const handleFrequencyCloseModal = () => {
    setIsFrequencyModalOpen(false);
  };
  const handleApprovalCloseModal = () => {
    setIsApprovalModalOpen(false);
  };

  const changeFreq = async(
    newFreq: string,
    dateStrategy?: 'current' | 'original',
  ) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `changed the frequency of the ticket screening from ${cur_case.data.frequency} to ${newFreq}`,
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      frequency: newFreq,
      activity: [ ...cur_case.data.activity, actObj ],
      ...(isTariffMonitor && dateStrategy ?
        { 'tariffResults.inputs.dateStrategy': dateStrategy } :
        {}),
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      const currentEmail = cur_user.basics?.email;
      const ownerEmail = cur_case.data.owner?.email;

      // iterate over sharedWith
      cur_case.data.sharedWith.forEach((email: string) => {
        if (email !== currentEmail) {
          // don’t notify the current user
          handleSettingsNotification(
            cur_case.data.ticketName,
            cur_user,
            email,
            cur_case.data._id,
            'frequency',
          );
        }
      });

      // If current user is in sharedWith but not the owner, also notify the owner
      if (
        cur_case.data.sharedWith.includes(currentEmail) &&
        currentEmail !== ownerEmail &&
        ownerEmail
      ) {
        handleSettingsNotification(
          cur_case.data.ticketName,
          cur_user,
          ownerEmail,
          cur_case.data._id,
          'frequency',
        );
      }
    } else {
      const error_msg = 'Error changing frequency.';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    setTrigger(!trigger);
  };

  const changeStatus = async(newStatus: string) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `changed the status of the ticket from ${cur_case.data.status} to ${newStatus}`,
      timeStamp: Date.now(),
    };
    const update: any = {
      _id: cur_case.data._id,
      status: newStatus,
      activity: [ ...cur_case.data.activity, actObj ],
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      const currentEmail = cur_user.basics?.email;
      const ownerEmail = cur_case.data.owner?.email;

      // iterate over sharedWith
      cur_case.data.sharedWith.forEach((email: string) => {
        if (email !== currentEmail) {
          // don’t notify the current user
          handleSettingsNotification(
            cur_case.data.ticketName,
            cur_user,
            email,
            cur_case.data._id,
            'status',
          );
        }
      });

      // If current user is in sharedWith but not the owner, also notify the owner
      if (
        cur_case.data.sharedWith.includes(currentEmail) &&
        currentEmail !== ownerEmail &&
        ownerEmail
      ) {
        handleSettingsNotification(
          cur_case.data.ticketName,
          cur_user,
          ownerEmail,
          cur_case.data._id,
          'status',
        );
      }
    } else {
      const error_msg = 'Error changing status.';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    setTrigger(!trigger);
  };

  const changeName = async(name: string) => {
    const actObj: any = {
      user: {
        kc_id: cur_user.basics?.id,
        email: cur_user.basics?.email,
        firstName: cur_user.basics?.firstName,
        lastName: cur_user.basics?.lastName,
      },
      action: `changed the name from ${cur_case.data.ticketName} to ${name}`,
      timeStamp: Date.now()
    };
    const update: any = {
      _id: cur_case.data._id,
      ticketName: name,
      activity: [ ...cur_case.data.activity, actObj ],
    };
    const res = await CasesAPI.updateCase(update);
    if (res?.success) {
      const currentEmail = cur_user.basics?.email;
      const ownerEmail = cur_case.data.owner?.email;

      // iterate over sharedWith
      cur_case.data.sharedWith.forEach((email: string) => {
        if (email !== currentEmail) {
          // don’t notify the current user
          handleSettingsNotification(
            cur_case.data.ticketName,
            cur_user,
            email,
            cur_case.data._id,
            'name',
          );
        }
      });

      // If current user is in sharedWith but not the owner, also notify the owner
      if (
        cur_case.data.sharedWith.includes(currentEmail) &&
        currentEmail !== ownerEmail &&
        ownerEmail
      ) {
        handleSettingsNotification(
          cur_case.data.ticketName,
          cur_user,
          ownerEmail,
          cur_case.data._id,
          'name',
        );
      }
    } else {
      const error_msg = 'Error changing name.';
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
    setTrigger(!trigger);
  };

  const handleDeleteTicket = async() => {
    const resp = await CasesAPI.deleteCase(cur_case.data._id);
    if (resp?.success) {
      cur_case.data.sharedWith.forEach((email: string) => {
        // Call to notification sending function here
        userDeleteTicketNotification(cur_case.data.ticketName, cur_user, email);
      });

      enqueueSnackbar('Removed case', { variant: 'info' });
      history.push('/bite-monitor');
    } else if (resp) {
      const error_msg = `Error removing case: ${resp?.result}`;
      enqueueSnackbar(error_msg, { variant: 'error' });
    }
  };

  const notificationTicket = async() => {
    // An empty preference reads as "never set" (both channels), so turning
    // every channel off is saved as an explicit ['None'].
    const chosen = (selectedOptsType || []).filter((opt: string) => opt !== 'None');
    const update: any = {
      _id: cur_case.data._id,
      notOptions: chosen.length ? chosen : [ 'None' ],
    };
    await CasesAPI.updateCase(update);
    setIsNotificationOpen(false);
    setTrigger(!trigger);
  };
  return (
    <>
      <div className='setting-table-cont'>
        <div className='setting-table-cont-sec'>
          <div className='setting-table-cont-sec-head'>
            <div className='setting-table-cont-sec-head-supporting'>
              <div className='setting-table-cont-sec-head-supporting-sec'>
                <div className='setting-table-cont-sec-head-supporting-sec-frame'>
                  <div className='setting-table-cont-sec-head-supporting-sec-frame-body'>
                    <div className='setting-table-cont-sec-head-supporting-sec-frame-body-text'>
                      Settings
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
          <div className='settings-table-frame'>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  Approval Status
                </div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <svg xmlns='http://www.w3.org/2000/svg' width='10' height='10' viewBox='0 0 10 10' fill='none'>
                  <circle cx='5' cy='5' r='4' fill={cur_case.data.status === 'Complete' ? 'var(--card-text)' : cur_case.data.status === 'Pending' ? '#FDB022' : cur_case.data.status === 'New Results' ? '#4895EF' : cur_case.data.status === 'Approved' ? '#47CD89' : cur_case.data.status === 'Rejected' ? '#F04438' : '#B67DFF' }/>
                </svg>
                <div className='settings-table-frame-row-cell1-text'>
                  {cur_case.data.status}
                </div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                  onClick={() => setIsApprovalModalOpen(true)}
                >
                  <g clipPath='url(#clip0_26064_29724)'>
                    <path d='M8.25 3.00017H5.1C3.83988 3.00017 3.20982 3.00017 2.72852 3.24541C2.30516 3.46112 1.96095 3.80533 1.74524 4.22869C1.5 4.70999 1.5 5.34005 1.5 6.60017V12.9002C1.5 14.1603 1.5 14.7903 1.74524 15.2716C1.96095 15.695 2.30516 16.0392 2.72852 16.2549C3.20982 16.5002 3.83988 16.5002 5.1 16.5002H11.4C12.6601 16.5002 13.2902 16.5002 13.7715 16.2549C14.1948 16.0392 14.539 15.695 14.7548 15.2716C15 14.7903 15 14.1603 15 12.9002V9.75017M5.99998 12.0002H7.25589C7.62277 12.0002 7.80622 12.0002 7.97885 11.9587C8.1319 11.922 8.27822 11.8614 8.41243 11.7791C8.5638 11.6864 8.69352 11.5567 8.95294 11.2972L16.125 4.12517C16.7463 3.50385 16.7463 2.49649 16.125 1.87517C15.5037 1.25385 14.4963 1.25385 13.875 1.87517L6.70293 9.04723C6.4435 9.30665 6.31378 9.43637 6.22102 9.58774C6.13878 9.72195 6.07817 9.86827 6.04143 10.0213C5.99998 10.194 5.99998 10.3774 5.99998 10.7443V12.0002Z' stroke='var(--text-primary)' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                  </g>
                  <defs>
                    <clipPath id='clip0_26064_29724'>
                      <rect width='18' height='18' fill='var(--text-primary)'/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  Screen Frequency
                </div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  {canSchedule ? cur_case.data.frequency : 'N/A'}
                </div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'>
                {canSchedule && <svg xmlns='http://www.w3.org/2000/svg' width='18' height='18' viewBox='0 0 18 18' fill='none' onClick={() => setIsFrequencyModalOpen(true)}>
                  <g clipPath='url(#clip0_26064_29724)'>
                    <path d='M8.25 3.00017H5.1C3.83988 3.00017 3.20982 3.00017 2.72852 3.24541C2.30516 3.46112 1.96095 3.80533 1.74524 4.22869C1.5 4.70999 1.5 5.34005 1.5 6.60017V12.9002C1.5 14.1603 1.5 14.7903 1.74524 15.2716C1.96095 15.695 2.30516 16.0392 2.72852 16.2549C3.20982 16.5002 3.83988 16.5002 5.1 16.5002H11.4C12.6601 16.5002 13.2902 16.5002 13.7715 16.2549C14.1948 16.0392 14.539 15.695 14.7548 15.2716C15 14.7903 15 14.1603 15 12.9002V9.75017M5.99998 12.0002H7.25589C7.62277 12.0002 7.80622 12.0002 7.97885 11.9587C8.1319 11.922 8.27822 11.8614 8.41243 11.7791C8.5638 11.6864 8.69352 11.5567 8.95294 11.2972L16.125 4.12517C16.7463 3.50385 16.7463 2.49649 16.125 1.87517C15.5037 1.25385 14.4963 1.25385 13.875 1.87517L6.70293 9.04723C6.4435 9.30665 6.31378 9.43637 6.22102 9.58774C6.13878 9.72195 6.07817 9.86827 6.04143 10.0213C5.99998 10.194 5.99998 10.3774 5.99998 10.7443V12.0002Z' stroke='var(--text-primary)' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                  </g>
                  <defs>
                    <clipPath id='clip0_26064_29724'>
                      <rect width='18' height='18' fill='var(--text-primary)'/>
                    </clipPath>
                  </defs>
                </svg>}
              </div>
            </div>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>Name</div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  {cur_case.data.ticketName}
                </div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                  onClick={() => setModalOpen(true)}
                >
                  <g clipPath='url(#clip0_26064_29724)'>
                    <path d='M8.25 3.00017H5.1C3.83988 3.00017 3.20982 3.00017 2.72852 3.24541C2.30516 3.46112 1.96095 3.80533 1.74524 4.22869C1.5 4.70999 1.5 5.34005 1.5 6.60017V12.9002C1.5 14.1603 1.5 14.7903 1.74524 15.2716C1.96095 15.695 2.30516 16.0392 2.72852 16.2549C3.20982 16.5002 3.83988 16.5002 5.1 16.5002H11.4C12.6601 16.5002 13.2902 16.5002 13.7715 16.2549C14.1948 16.0392 14.539 15.695 14.7548 15.2716C15 14.7903 15 14.1603 15 12.9002V9.75017M5.99998 12.0002H7.25589C7.62277 12.0002 7.80622 12.0002 7.97885 11.9587C8.1319 11.922 8.27822 11.8614 8.41243 11.7791C8.5638 11.6864 8.69352 11.5567 8.95294 11.2972L16.125 4.12517C16.7463 3.50385 16.7463 2.49649 16.125 1.87517C15.5037 1.25385 14.4963 1.25385 13.875 1.87517L6.70293 9.04723C6.4435 9.30665 6.31378 9.43637 6.22102 9.58774C6.13878 9.72195 6.07817 9.86827 6.04143 10.0213C5.99998 10.194 5.99998 10.3774 5.99998 10.7443V12.0002Z' stroke='var(--text-primary)' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                  </g>
                  <defs>
                    <clipPath id='clip0_26064_29724'>
                      <rect width='18' height='18' fill='var(--text-primary)'/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>Type</div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  {cur_case.data.docType}
                </div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'></div>
            </div>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  Favorited
                </div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  {cur_case.data.favorite ? 'Yes' : 'No'}
                </div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'></div>
            </div>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  Source Document
                </div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>{cur_case.data.documents.length > 0 ? cur_case.data.documents[0].uploadedFile.name : 'No Documents'}</div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                  onClick={() => setAutoTab('Documents')}
                >
                  <g clipPath='url(#clip0_26064_29724)'>
                    <path d='M8.25 3.00017H5.1C3.83988 3.00017 3.20982 3.00017 2.72852 3.24541C2.30516 3.46112 1.96095 3.80533 1.74524 4.22869C1.5 4.70999 1.5 5.34005 1.5 6.60017V12.9002C1.5 14.1603 1.5 14.7903 1.74524 15.2716C1.96095 15.695 2.30516 16.0392 2.72852 16.2549C3.20982 16.5002 3.83988 16.5002 5.1 16.5002H11.4C12.6601 16.5002 13.2902 16.5002 13.7715 16.2549C14.1948 16.0392 14.539 15.695 14.7548 15.2716C15 14.7903 15 14.1603 15 12.9002V9.75017M5.99998 12.0002H7.25589C7.62277 12.0002 7.80622 12.0002 7.97885 11.9587C8.1319 11.922 8.27822 11.8614 8.41243 11.7791C8.5638 11.6864 8.69352 11.5567 8.95294 11.2972L16.125 4.12517C16.7463 3.50385 16.7463 2.49649 16.125 1.87517C15.5037 1.25385 14.4963 1.25385 13.875 1.87517L6.70293 9.04723C6.4435 9.30665 6.31378 9.43637 6.22102 9.58774C6.13878 9.72195 6.07817 9.86827 6.04143 10.0213C5.99998 10.194 5.99998 10.3774 5.99998 10.7443V12.0002Z' stroke='var(--text-primary)' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                  </g>
                  <defs>
                    <clipPath id='clip0_26064_29724'>
                      <rect width='18' height='18' fill='var(--text-primary)'/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  Notification Preference
                </div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'>
                  {Array.isArray(selectedOptsType) ?
                    selectedOptsType.join(', ') :
                    ''}
                </div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'>
                <svg
                  xmlns='http://www.w3.org/2000/svg'
                  width='18'
                  height='18'
                  viewBox='0 0 18 18'
                  fill='none'
                  onClick={() => setIsNotificationOpen(true)}
                >
                  <g clipPath='url(#clip0_26064_29724)'>
                    <path d='M8.25 3.00017H5.1C3.83988 3.00017 3.20982 3.00017 2.72852 3.24541C2.30516 3.46112 1.96095 3.80533 1.74524 4.22869C1.5 4.70999 1.5 5.34005 1.5 6.60017V12.9002C1.5 14.1603 1.5 14.7903 1.74524 15.2716C1.96095 15.695 2.30516 16.0392 2.72852 16.2549C3.20982 16.5002 3.83988 16.5002 5.1 16.5002H11.4C12.6601 16.5002 13.2902 16.5002 13.7715 16.2549C14.1948 16.0392 14.539 15.695 14.7548 15.2716C15 14.7903 15 14.1603 15 12.9002V9.75017M5.99998 12.0002H7.25589C7.62277 12.0002 7.80622 12.0002 7.97885 11.9587C8.1319 11.922 8.27822 11.8614 8.41243 11.7791C8.5638 11.6864 8.69352 11.5567 8.95294 11.2972L16.125 4.12517C16.7463 3.50385 16.7463 2.49649 16.125 1.87517C15.5037 1.25385 14.4963 1.25385 13.875 1.87517L6.70293 9.04723C6.4435 9.30665 6.31378 9.43637 6.22102 9.58774C6.13878 9.72195 6.07817 9.86827 6.04143 10.0213C5.99998 10.194 5.99998 10.3774 5.99998 10.7443V12.0002Z' stroke='var(--text-primary)' strokeWidth='1.5' strokeLinecap='round' strokeLinejoin='round'/>
                  </g>
                  <defs>
                    <clipPath id='clip0_26064_29724'>
                      <rect width='18' height='18' fill='var(--text-primary)'/>
                    </clipPath>
                  </defs>
                </svg>
              </div>
            </div>
            <div className='settings-table-frame-row'>
              <div className='settings-table-frame-row-cell1'>
                <div className='audit-doc-tabe-row-frame-left-texts-trash' onClick={() => setIsDeleteModalOpen(true)}>Delete</div>
              </div>
              <div className='settings-table-frame-row-cell1'>
                <div className='settings-table-frame-row-cell1-text'></div>
              </div>
              <div className='settings-table-frame-row-cell1-icons'></div>
            </div>
          </div>
        </div>
      </div>
      <Modal open={isModalOpen} onClose={handleCloseModal}>
        <ChangeTicketName
          handleClose={handleCloseModal}
          changeName={changeName}
        />
      </Modal>
      <Modal
        open={isFrequencyModalOpen && canSchedule}
        onClose={handleFrequencyCloseModal}
      >
        <ScreeningFrequency
          handleClose={handleFrequencyCloseModal}
          changeFrequency={changeFreq}
          curFreq={cur_case.data.frequency}
          curDateStrategy={isTariffMonitor ? tariffDateStrategy : undefined}
        />
      </Modal>
      <Modal open={isApprovalModalOpen} onClose={handleApprovalCloseModal}>
        <ApprovalStatus
          handleClose={handleApprovalCloseModal}
          changeStatus={changeStatus}
          curStatus={cur_case.data.status}
        />
      </Modal>
      <Modal
        open={isDeleteModalOpen}
        onClose={() => setIsDeleteModalOpen(false)}
      >
        <DeleteCommonModal
          handleClose={() => setIsDeleteModalOpen(false)}
          handleDelete={handleDeleteTicket}
          title={'Delete?'}
          paragraph={'Once Deleted there is no way to recover this or the infomation saved on it.'}/>
      </Modal>
      <Modal
        open={isNotificationOpen}
        onClose={() => setIsNotificationOpen(false)}
      >
        <NotificationsModal
          selectedOptsType={selectedOptsType}
          setSelectedOptsType={setSelectedOptsType}
          handleClose={() => setIsNotificationOpen(false)}
          handleSave={notificationTicket}
        />
      </Modal>
    </>
  );
};

export default Settings;
